import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material';
import { useMount } from '@provider-portal/common/hooks/useMount';
import { LanguageUtils, SupportedLanguages } from '@provider-portal/common/utils/translations';
import {
  LocaleToLocalizedLanguageMap,
  LocaleType,
} from '@provider-portal/common/utils/translations_types';
import { sortBy } from 'lodash';

import { getAppLocale, setAppLocale } from 'utils/translations';

import { stopTouchEventsPropagation } from '../../../../common/all/utils/stopTouchEventsPropagation';

const styles = {
  form: {
    backgroundColor: 'colors.neutral200',
    borderRadius: 2,
    '& .MuiOutlinedInput-root': { minHeight: 36 },
  },
};

const LanguageSelector: React.FC = () => {
  const intl = useIntl();
  const [locale] = useState(getAppLocale());

  const onChange = useCallback(async ({ target: { value } }) => {
    // If selected value is validLocale
    if (LanguageUtils.getIsValidLocale(value)) {
      await setAppLocale(value, true);
    } else {
      // TODO Alert, locale change failed
    }
  }, []);

  useMount(async () => {
    // It's an easy hack to force correct locale on page reload
    if (locale !== intl.locale) {
      await setAppLocale(locale, false);
    }
  });

  const getSelectIcon = useCallback((props) => <ExpandMoreIcon color="secondary" {...props} />, []);

  const sortedLanguageOptions = sortBy(
    SupportedLanguages,
    (lang) => LocaleToLocalizedLanguageMap[LocaleType[lang]],
  );

  const languageLabel = intl.formatMessage({ id: 'components.LanguageSelector.language' });
  return (
    <Box mt={1} minWidth={16}>
      <Typography variant="caption">{languageLabel}</Typography>
      <FormControl variant="outlined" fullWidth margin="dense" sx={styles.form}>
        <Select
          labelId="ui-language-selector-label"
          value={locale}
          onChange={onChange}
          IconComponent={getSelectIcon}
          {...stopTouchEventsPropagation()}
        >
          {sortedLanguageOptions.map((lang) => (
            <MenuItem key={LocaleType[lang]} value={LocaleType[lang]}>
              {LocaleToLocalizedLanguageMap[LocaleType[lang]]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default LanguageSelector;
